import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { useDataStore } from "@/stores/data";
import { Domain } from "@/lib/api";
import { BillingStatusBanner } from "@/components/billing/BillingStatusBanner";
import { Modal } from "@/components/ui/Modal";
import { OrgSwitcher } from "@/components/layout/OrgSwitcher";
import { trackHelpPageVisit } from "@/lib/ux-analytics";
import {
  LayoutDashboard,
  Globe,
  Send,
  AlertTriangle,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Building2,
  Users,
  CreditCard,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Settings,
  Search,
  ShieldCheck,
  HelpCircle,
  Menu,
  X,
  BadgeCheck,
  Briefcase,
  Wrench,
  Sparkles,
} from "lucide-react";

const TIER_LIMITS = {
  free: { maxDomains: 1, maxUsers: 1 },
  starter: { maxDomains: 5, maxUsers: 3 },
  pro: { maxDomains: 100, maxUsers: 10 }, // Base 25, up to 100 with overage
  enterprise: { maxDomains: 250, maxUsers: 25 },
  enterprise_plus: { maxDomains: Infinity, maxUsers: Infinity },
} as const;

// Navigation item type
type NavItem = {
  id: string;
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  enterprise?: boolean;
  requiresEnforcement?: boolean;
  badge?: "alerts"; // Special badge type
  matchPaths?: string[]; // Additional paths that make this item active
};

// Navigation section type
type NavSection = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  items: NavItem[];
};

// =============================================================================
// NAVIGATION STRUCTURE (Phase 4 IA Cleanup)
// =============================================================================
// Simplified, flat navigation:
// - Main items shown directly (no groups)
// - Enforcement/BIMI/Remediation moved to domain detail tabs
// - MSP Portal at bottom (only for MSP orgs)
// - Settings collapsed by default
// =============================================================================

// Main navigation items - shown directly without grouping
const mainNavSection: NavSection = {
  id: "main",
  label: "Main",
  icon: LayoutDashboard,
  collapsible: false, // Don't collapse - always visible
  defaultCollapsed: false,
  items: [
    { id: "dashboard", path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "domains", path: "/domains", label: "Domains", icon: Globe },
    { id: "sources", path: "/sources", label: "Sources", icon: Send },
    { id: "reports", path: "/reports", label: "Reports", icon: FileText },
    { id: "alerts", path: "/alerts", label: "Action Items", icon: AlertTriangle, badge: "alerts" },
    { id: "audit", path: "/audit", label: "Audit", icon: ClipboardList, enterprise: true },
    { id: "help", path: "/help", label: "Help", icon: HelpCircle },
  ],
};

// Settings section - expanded by default, two top-level items (Personal, Organization)
const settingsSection: NavSection = {
  id: "settings",
  label: "Settings",
  icon: Settings,
  collapsible: true,
  defaultCollapsed: false,
  items: [
    {
      id: "settings-personal",
      path: "/settings",
      label: "Personal",
      icon: User,
      matchPaths: ["/settings/notifications", "/settings/security"],
    },
    {
      id: "settings-org",
      path: "/settings/organization",
      label: "Organization",
      icon: Building2,
      matchPaths: ["/settings/billing", "/settings/team", "/settings/sso"],
    },
  ],
};

// MSP Portal section - only shown for MSP organizations, at bottom
const mspPortalSection: NavSection = {
  id: "msp-portal",
  label: "MSP Portal",
  icon: Briefcase,
  collapsible: true,
  defaultCollapsed: true, // Collapsed by default
  items: [
    { id: "msp-overview", path: "/msp", label: "Overview", icon: LayoutDashboard },
    { id: "msp-clients", path: "/msp/clients", label: "Clients", icon: Building2 },
    { id: "msp-billing", path: "/msp/billing", label: "Billing", icon: CreditCard },
    { id: "msp-white-label", path: "/msp/white-label", label: "White-Label", icon: Sparkles },
    { id: "msp-team", path: "/msp/team", label: "Team", icon: Users },
  ],
};

// Legacy sections kept for backwards compatibility (routes still work)
// These items are accessible but not shown in main navigation
const legacySection: NavSection = {
  id: "legacy",
  label: "Legacy",
  icon: Wrench,
  collapsible: true,
  defaultCollapsed: true,
  items: [
    { id: "remediation", path: "/alerts?tab=tasks", label: "Remediation", icon: ClipboardCheck },
    { id: "enforcement", path: "/enforcement", label: "Enforcement", icon: ShieldCheck },
    { id: "bimi", path: "/bimi", label: "BIMI", icon: BadgeCheck, enterprise: true, requiresEnforcement: true },
  ],
};

// Legacy flat lists for search functionality (includes all items for command palette)
const allNavItems = [
  ...mainNavSection.items,
  ...settingsSection.items,
  ...mspPortalSection.items,
  ...legacySection.items, // Include legacy items so they're still searchable
];

// Collapsible navigation section component
function NavSectionHeader({
  section,
  isOpen,
  onToggle,
  collapsed,
}: {
  section: NavSection;
  isOpen: boolean;
  onToggle: () => void;
  collapsed: boolean;
}) {
  if (collapsed) return null;

  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider hover:text-white/70 transition-colors"
    >
      <span>{section.label}</span>
      <ChevronDown
        className={`w-4 h-4 transition-transform ${isOpen ? "" : "-rotate-90"}`}
      />
    </button>
  );
}

function NavItem({
  item,
  isActive,
  collapsed,
  alertCount,
}: {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
  alertCount?: number;
}) {
  return (
    <Link
      to={item.path}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
        isActive
          ? "bg-amber-500/20 text-amber-400"
          : "text-white/80 hover:bg-white/10 hover:text-white"
      }`}
    >
      <item.icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 text-left font-medium">{item.label}</span>
          {item.badge === "alerts" && alertCount && alertCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500 text-slate-900">
              {alertCount}
            </span>
          )}
        </>
      )}
    </Link>
  );
}

function NavSections({
  isMsp,
  isEnterprise,
  hasDomainsAtEnforcement,
  alertCount,
  sidebarCollapsed,
  currentPath,
}: {
  isMsp: boolean;
  isEnterprise: boolean;
  hasDomainsAtEnforcement: boolean;
  alertCount: number;
  sidebarCollapsed: boolean;
  currentPath: string;
}) {
  // Track collapsed state for each section — derived from defaultCollapsed on each section definition
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => ({
    [mainNavSection.id]: mainNavSection.defaultCollapsed ?? false,
    [settingsSection.id]: settingsSection.defaultCollapsed ?? false,
    [mspPortalSection.id]: mspPortalSection.defaultCollapsed ?? false,
    [legacySection.id]: legacySection.defaultCollapsed ?? false,
  }));

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Filter items based on enterprise/enforcement requirements
  const filterItems = (items: NavItem[]) => {
    return items.filter((item) => {
      if (item.enterprise && !isEnterprise) return false;
      if (item.requiresEnforcement && !hasDomainsAtEnforcement) return false;
      return true;
    });
  };

  const isItemActive = (item: NavItem) => {
    // For the root /settings path, only exact match (startsWith would match all /settings/* routes)
    const exactOrPrefix =
      item.path === "/settings"
        ? currentPath === "/settings"
        : currentPath === item.path || currentPath.startsWith(item.path + "/");
    return (
      exactOrPrefix ||
      (item.matchPaths?.some(
        (p) => currentPath === p || currentPath.startsWith(p + "/")
      ) ?? false)
    );
  };

  // Build sections based on user context
  // New IA: Main items (flat), Settings, then MSP Portal at bottom
  const sections: NavSection[] = [];

  // Main navigation - shown for all users (flat, no grouping)
  sections.push(mainNavSection);

  // Settings - for all users (collapsed by default)
  sections.push(settingsSection);

  // MSP Portal - only for MSP users (at bottom, collapsed by default)
  if (isMsp) {
    sections.push(mspPortalSection);
  }

  // Note: Legacy items (Enforcement, BIMI, Remediation) are not shown in nav
  // but routes still work and items are searchable in command palette

  return (
    <div className="space-y-2">
      {sections.map((section, index) => {
        const filteredItems = filterItems(section.items);
        if (filteredItems.length === 0) return null;

        const isOpen = !collapsedSections[section.id];

        return (
          <div key={section.id}>
            {/* Section divider (except for first section) */}
            {index > 0 && !sidebarCollapsed && (
              <div className="border-t border-white/10 my-3" />
            )}

            {/* Section header */}
            {section.collapsible && (
              <NavSectionHeader
                section={section}
                isOpen={isOpen}
                onToggle={() => toggleSection(section.id)}
                collapsed={sidebarCollapsed}
              />
            )}

            {/* Section items */}
            {(isOpen || sidebarCollapsed) && (
              <div className="space-y-1">
                {filteredItems.map((item) => (
                  <NavItem
                    key={item.id}
                    item={item}
                    isActive={isItemActive(item)}
                    collapsed={sidebarCollapsed}
                    alertCount={item.badge === "alerts" ? alertCount : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    free: "bg-gray-500/20 text-gray-300",
    starter: "bg-amber-500/20 text-amber-400",
    pro: "bg-blue-500/20 text-blue-500",
    enterprise: "bg-purple-500/20 text-purple-500",
    enterprise_plus: "bg-purple-500/20 text-amber-300",
  };

  const displayName = tier === "enterprise_plus" ? "Enterprise+" : tier;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wide ${colors[tier] || colors.free}`}>
      {displayName}
    </span>
  );
}

function EnforcementProgress({ percentage }: { percentage: number }) {
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative w-12 h-12">
      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white">
        {percentage}%
      </span>
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization, logout, isMsp } = useAuthStore();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{ domains: Domain[]; pages: NavItem[] }>({ domains: [], pages: [] });
  const [domains, setDomains] = useState<Domain[]>([]);

  // Subscribe to alerts and remediation tasks from data store for real-time updates
  const storeAlerts = useDataStore((state) => state.alerts);
  const ensureAlerts = useDataStore((state) => state.ensureAlerts);
  const storeRemediationTasks = useDataStore((state) => state.remediationTasks);
  const ensureRemediationTasks = useDataStore((state) => state.ensureRemediationTasks);
  const actionItemsSummary = useDataStore((state) => state.actionItemsSummary);
  const ensureActionItemsSummary = useDataStore((state) => state.ensureActionItemsSummary);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Update document title based on current page
  useEffect(() => {
    const currentItem = allNavItems.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(item.path + "/")
    );
    const pageTitle = currentItem?.label || "Dashboard";
    document.title = `${pageTitle} | Verkh`;
  }, [location.pathname]);

  // Real data from API
  const [domainCount, setDomainCount] = useState(0);
  const [enforcementProgress, setEnforcementProgress] = useState(0);
  const [hasDomainsAtEnforcement, setHasDomainsAtEnforcement] = useState(false);

  // For notifications dropdown, filter out alerts that are covered by AI insights on the Action Items page
  // These patterns match alert_types that the insight pipeline also generates, avoiding confusion
  const alerts = useMemo(() => storeAlerts.filter((a) => {
    const coveredPatterns = [
      /has authentication issues$/i,
      /^Configure .+ (?:DKIM|SPF)/i,
      /^Add .+ to .+ (?:SPF|DKIM) record/i,
    ];
    return !coveredPatterns.some((p) => p.test(a.title));
  }), [storeAlerts]);
  const openAlertCount = alerts.filter((a) => a.status === "open").length;
  const pendingTaskCount = storeRemediationTasks.filter(
    (t) => t.status === "pending" || t.status === "in_progress"
  ).length;
  // Use unified action items summary count, falling back to legacy alert+task count
  const alertCount = actionItemsSummary?.counts.total ?? (openAlertCount + pendingTaskCount);

  // Helper to calculate enforcement from domain data
  const calculateEnforcement = useCallback((domainData: Domain[]) => {
    setDomains(domainData);
    setDomainCount(domainData.length);
    // Calculate enforcement progress based on all domains' DMARC policy
    // p=none = 0 points, p=quarantine = 50 points, p=reject = 100 points
    if (domainData.length > 0) {
      const totalPoints = domainData.reduce((sum, d) => {
        const policy = d.current_policy || d.dmarc_policy || "none";
        if (policy === "reject") return sum + 100;
        if (policy === "quarantine") return sum + 50;
        return sum; // p=none = 0 points
      }, 0);
      const maxPoints = domainData.length * 100;
      setEnforcementProgress(Math.round((totalPoints / maxPoints) * 100));

      // Check if any domains are at full enforcement (p=reject)
      const domainsAtReject = domainData.filter(d => {
        const policy = d.current_policy || d.dmarc_policy || "none";
        return policy === "reject";
      });
      setHasDomainsAtEnforcement(domainsAtReject.length > 0);
    } else {
      setEnforcementProgress(0);
      setHasDomainsAtEnforcement(false);
    }
  }, []);

  useEffect(() => {
    // Fetch alerts and remediation tasks using data store (enables real-time sync across components)
    ensureAlerts().catch((err) => console.error("Failed to load alerts:", err));
    ensureRemediationTasks().catch((err) => console.error("Failed to load remediation tasks:", err));
    ensureActionItemsSummary().catch((err) => console.error("Failed to load action items summary:", err));

    // Fetch domains using data store
    const store = useDataStore.getState();
    store.ensureDomains()
      .then((domainData) => {
        calculateEnforcement(domainData);
      })
      .catch((err) => console.error("Failed to load domains:", err));
  }, [calculateEnforcement, ensureAlerts, ensureRemediationTasks, ensureActionItemsSummary]);

  // Subscribe to domain changes in the data store
  useEffect(() => {
    let previousDomains = useDataStore.getState().domains;
    const unsubscribe = useDataStore.subscribe((state) => {
      const currentDomains = state.domains;
      // Only recalculate if domains array has changed
      if (currentDomains !== previousDomains) {
        previousDomains = currentDomains;
        if (currentDomains.length > 0 || domains.length > 0) {
          calculateEnforcement(currentDomains);
        }
      }
    });
    return unsubscribe;
  }, [calculateEnforcement, domains.length]);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus search input when modal opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Search logic
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults({ domains: [], pages: [] });
      return;
    }
    const lowerQuery = query.toLowerCase();

    // Search domains
    const matchedDomains = domains.filter(d =>
      d.domain_name.toLowerCase().includes(lowerQuery)
    ).slice(0, 5);

    // Search pages
    const matchedPages = allNavItems.filter(p =>
      p.label.toLowerCase().includes(lowerQuery)
    );

    setSearchResults({ domains: matchedDomains, pages: matchedPages });
  }, [domains]);

  const handleSearchSelect = (path: string) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const tier = organization?.billing_tier || "free";
  const tierLimit = TIER_LIMITS[tier as keyof typeof TIER_LIMITS] || TIER_LIMITS.free;

  return (
    <div className="flex h-screen bg-white font-sans">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:relative inset-y-0 left-0 z-50
          flex flex-col transition-all duration-300
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${sidebarCollapsed ? "md:w-20" : "md:w-64"} w-64
        `}
        style={{ backgroundColor: "#1a1f2e" }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img
              src="/logo-dark.svg"
              alt="Verkh"
              className="w-9 h-9 rounded-lg"
            />
            {!sidebarCollapsed && (
              <span className="text-white font-bold text-lg font-display leading-tight">Verkh</span>
            )}
          </Link>
          {/* Mobile close button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="md:hidden p-2 text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          <NavSections
            isMsp={isMsp}
            isEnterprise={tier === "enterprise" || tier === "enterprise_plus"}
            hasDomainsAtEnforcement={hasDomainsAtEnforcement}
            alertCount={alertCount}
            sidebarCollapsed={sidebarCollapsed}
            currentPath={location.pathname}
          />
        </nav>

        {/* Enforcement Progress */}
        {!sidebarCollapsed && (
          <Link
            to="/domains?filter=enforcement"
            className="block p-4 mx-3 mb-3 rounded-xl bg-white/5 hover:bg-slate-700/50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3 mb-3">
              <EnforcementProgress percentage={enforcementProgress} />
              <div>
                <div className="text-sm font-medium text-white">Enforcement</div>
                <div className="text-xs text-white/60">toward p=reject</div>
              </div>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-1000"
                style={{ width: `${enforcementProgress}%` }}
              />
            </div>
          </Link>
        )}

        {/* Tier info */}
        <div className="p-4 border-t border-white/10">
          {sidebarCollapsed ? (
            <div className="flex justify-center">
              <TierBadge tier={tier} />
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <TierBadge tier={tier} />
                <Link to="/domains" className="block text-xs text-white/50 hover:text-white/70 transition-colors mt-1">
                  {domainCount}/{tierLimit.maxDomains === Infinity ? "∞" : tierLimit.maxDomains} domains
                </Link>
              </div>
              <Link to="/settings/billing" className="text-xs text-amber-400 hover:text-amber-300 font-medium">
                Upgrade
              </Link>
            </div>
          )}
        </div>

        {/* Collapse button - hidden on mobile */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden md:flex h-12 border-t border-white/10 items-center justify-center text-white/60 hover:text-white transition-colors"
        >
          {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger menu */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-slate-600 hover:text-slate-800"
            >
              <Menu className="w-6 h-6" />
            </button>
            {organization?.logo_url ? (
              <img
                src={organization.logo_url}
                alt={organization.name || "Organization"}
                className="h-8 max-w-[200px] object-contain"
              />
            ) : (
              <h2 className="text-lg font-semibold text-slate-800 font-display truncate">{organization?.name || "Dashboard"}</h2>
            )}
            {/* MSP Organization Switcher */}
            {isMsp && (
              <div className="hidden md:block">
                <OrgSwitcher />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 h-10 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors border border-slate-200"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Search...</span>
              <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-slate-100 rounded text-slate-500 font-mono">
                Ctrl K
              </kbd>
            </button>

            {/* Help - Quick access */}
            <Link
              to="/help"
              onClick={() => trackHelpPageVisit(location.pathname)}
              className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              title="Help & Documentation"
            >
              <HelpCircle className="w-5 h-5" />
            </Link>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {alertCount > 0 && (
                  <span
                    className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full text-xs font-medium flex items-center justify-center text-white bg-amber-500"
                  >
                    {alertCount > 9 ? "9+" : alertCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 animate-fade-in">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-800">Alerts</span>
                    <span className="text-xs text-slate-400">{openAlertCount} unread</span>
                  </div>
                  {alerts.length > 0 ? (
                    <div className="max-h-80 overflow-y-auto">
                      {alerts.slice(0, 5).map((alert) => (
                        <Link
                          key={alert.id}
                          to={`/alerts?id=${alert.id}`}
                          onClick={() => setNotificationsOpen(false)}
                          className="block px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                              alert.severity === "critical" ? "bg-red-600" :
                              alert.severity === "warning" ? "bg-amber-500" : "bg-blue-500"
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">
                                {alert.title}
                              </div>
                              <div className="text-xs text-slate-500 truncate">
                                {alert.domain_name}
                              </div>
                              <div className="text-xs text-slate-400 mt-1">
                                {new Date(alert.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-500 text-sm">
                      No new alerts
                    </div>
                  )}
                  <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
                    <Link
                      to="/alerts"
                      onClick={() => setNotificationsOpen(false)}
                      className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                    >
                      View all alerts &rarr;
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-4 pr-2 sm:pr-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-slate-900 font-semibold text-sm">
                  {user?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("") || "?"}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-slate-700">{user?.name || "User"}</div>
                  <div className="text-xs text-slate-400 capitalize">{user?.role || "member"}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-14 w-52 sm:w-56 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 animate-fade-in">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="font-medium text-slate-800">{user?.name}</div>
                    <div className="text-sm text-slate-500">{user?.email}</div>
                  </div>
                  <div className="py-2">
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-3"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                    <Link
                      to="/settings/notifications"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-3"
                    >
                      <Bell className="w-4 h-4" />
                      Alert Settings
                    </Link>
                  </div>
                  <div className="py-2 border-t border-slate-100">
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Billing status banner */}
        {organization && (
          <BillingStatusBanner
            organization={organization}
            isOwner={user?.role === "owner"}
          />
        )}

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 md:p-6">{children}</main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-slate-50 px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <span>Â© {new Date().getFullYear()} Verkh, LLC.</span>
              <span className="hidden sm:inline">All rights reserved.</span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://verkh.io/terms/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-700 transition-colors"
              >
                Terms
              </a>
              <a
                href="https://verkh.io/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-700 transition-colors"
              >
                Privacy
              </a>
              <a
                href="https://verkh.io/cookies/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-700 transition-colors"
              >
                Cookies
              </a>
              <a
                href="https://verkh.io/refunds/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-slate-700 transition-colors"
              >
                Refunds
              </a>
              <a
                href="mailto:support@verkh.io"
                className="hover:text-slate-700 transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </footer>
      </div>

      {/* Click outside to close menus */}
      {(userMenuOpen || notificationsOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setUserMenuOpen(false);
            setNotificationsOpen(false);
          }}
        />
      )}

      {/* Search Modal */}
      <Modal
        open={searchOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSearchOpen(false);
            setSearchQuery("");
          }
        }}
      >
        <Modal.Content
          hideCloseButton
          size="xl"
          className="!top-[20vh] !translate-y-0 border border-slate-200"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search domains, pages..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="flex-1 py-4 text-lg outline-none placeholder:text-slate-400"
            />
            <kbd className="px-2 py-1 text-xs bg-slate-100 rounded text-slate-500 font-mono">
              ESC
            </kbd>
          </div>

          {/* Search results */}
          <div className="max-h-80 overflow-y-auto">
            {searchQuery ? (
              <>
                {/* Domain results */}
                {searchResults.domains.length > 0 && (
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Domains
                    </div>
                    {searchResults.domains.map((domain) => (
                      <button
                        key={domain.id}
                        onClick={() => handleSearchSelect(`/domains/${domain.id}`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left"
                      >
                        <Globe className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{domain.domain_name}</span>
                        <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
                          domain.verification_status === "verified"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {domain.verification_status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Page results */}
                {searchResults.pages.length > 0 && (
                  <div className="p-2 border-t border-slate-100">
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Pages
                    </div>
                    {searchResults.pages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => handleSearchSelect(page.path)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left"
                      >
                        <page.icon className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{page.label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* No results */}
                {searchResults.domains.length === 0 && searchResults.pages.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No results for "{searchQuery}"
                  </div>
                )}
              </>
            ) : (
              /* Quick actions when no query */
              <div className="p-2">
                <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Quick Actions
                </div>
                <button
                  onClick={() => handleSearchSelect("/domains")}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left"
                >
                  <Globe className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">View all domains</span>
                </button>
                <button
                  onClick={() => handleSearchSelect("/alerts")}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left"
                >
                  <AlertTriangle className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">View alerts</span>
                  {alertCount > 0 && (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                      {alertCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleSearchSelect("/settings")}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 text-left"
                >
                  <Settings className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">Settings</span>
                </button>
              </div>
            )}
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
}